import { useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Camera, Loader2, MessageSquare, Users, X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { FilePondUpload } from '@/components/file-pond-upload'
import {
  getListCommunitiesQueryKey,
  getListMyCommunitiesQueryKey,
  useCreateCommunity,
  useJoinCommunity,
  useLeaveCommunity,
  useListCommunities,
  useListMyCommunities,
} from '@/utils/api/membership-service'

import { useUploadFile } from '@/utils/api/file-service'
import type { CommunityDTO } from '@/utils/api/membership-service/models/community-dt-o'

const DEFAULT_CREATE_VISIBILITY = true
const DISCOVER_PAGE_SIZE = 8
const SEARCH_DEBOUNCE_MS = 300

type CommunityActionType = 'join' | 'leave'

type ActiveAction = { id: number; type: CommunityActionType } | null

function getInitials(name?: string) {
  if (!name) return 'C'
  const initials = name
    .split(' ')
    .map((segment) => segment.charAt(0))
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()

  return initials || 'C'
}

function CommunityCard({
  community,
  isMember,
  onAction,
  actionLabel,
  actionLoading,
  canVisit,
}: {
  community: CommunityDTO
  isMember?: boolean
  onAction: () => void
  actionLabel: string
  actionLoading?: boolean
  canVisit?: boolean
}) {
  return (
    <div className="flex flex-col gap-3 rounded-2xl border bg-card p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <Avatar>
          {community.iconUrl ? (
            <AvatarImage src={community.iconUrl} alt={community.name} />
          ) : (
            <AvatarFallback>{getInitials(community.name)}</AvatarFallback>
          )}
        </Avatar>
        <div className="flex-1 space-y-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold leading-tight">
              {community.name ?? 'Untitled community'}
            </p>
          </div>
          <p className="text-xs text-muted-foreground">
            {community.topic ?? 'Community discussion board'}
          </p>
        </div>
      </div>
      <p className="text-sm text-muted-foreground line-clamp-3">
        {community.description ?? 'Share what this community is about so others can decide to join.'}
      </p>
      <div className="rounded-xl bg-muted/60 p-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {community.membersCount ?? 0} members
          </span>
          <div className="flex flex-1 flex-wrap justify-end gap-2">
            {canVisit ? (
              <Button
                asChild
                size="sm"
                variant="outline"
                className="border-primary/40 text-primary shadow-none hover:bg-primary/10"
              >
                <Link to="/benefits/communities/$communityId" params={{ communityId: String(community.id) }}>
                  <MessageSquare className="mr-2 size-4" />
                  Open discussions
                </Link>
              </Button>
            ) : null}
            <Button
              size="sm"
              variant={isMember ? 'outline' : 'default'}
              onClick={onAction}
              className="min-w-[112px] justify-center text-xs"
              disabled={actionLoading}
            >
              {actionLoading && <Loader2 className="size-4 animate-spin" />}
              {actionLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export function CommunitiesPage() {
  const queryClient = useQueryClient()
  const uploadMutation = useUploadFile()
  const [activeAction, setActiveAction] = useState<ActiveAction>(null)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [createName, setCreateName] = useState('')
  const [createDescription, setCreateDescription] = useState('')
  const [createIsPublic, setCreateIsPublic] = useState(DEFAULT_CREATE_VISIBILITY)
  const [createIconUrl, setCreateIconUrl] = useState('')
  const [createCoverImageUrl, setCreateCoverImageUrl] = useState('')
  const [iconPreview, setIconPreview] = useState<string | null>(null)
  const [coverPreview, setCoverPreview] = useState<string | null>(null)
  const [discoverSearch, setDiscoverSearch] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [discoverPage, setDiscoverPage] = useState(0)
  const [loadedPages, setLoadedPages] = useState<Record<number, CommunityDTO[]>>({})
  const [isFetchingNext, setIsFetchingNext] = useState(false)
  const isReadOnly = false
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const handler = setTimeout(() => {
      const normalizedSearch = discoverSearch.trim()
      setSearchQuery(normalizedSearch)
      setDiscoverPage(0)
      setLoadedPages({})
      setIsFetchingNext(false)
    }, SEARCH_DEBOUNCE_MS)

    return () => clearTimeout(handler)
  }, [discoverSearch])

  const discoverParams = useMemo(
    () => ({
      page: discoverPage,
      size: DISCOVER_PAGE_SIZE,
      search: searchQuery || undefined,
    }),
    [discoverPage, searchQuery],
  )

  const communitiesQuery = useListCommunities(discoverParams, {
    query: { staleTime: 1000 * 60 * 5 },
  })
  const myCommunitiesQuery = useListMyCommunities(undefined, { query: { staleTime: 1000 * 60 * 5 } })

  const myCommunities = myCommunitiesQuery.data?.data?.content ?? []

  const joinedCommunityIds = useMemo(() => {
    const ids: number[] = []
    for (const community of myCommunities) {
      if (typeof community.id === 'number') ids.push(community.id)
    }
    return new Set(ids)
  }, [myCommunities])

  useEffect(() => {
    const data = communitiesQuery.data?.data
    if (!data) return
    const pageNumber = data.number ?? 0
    const pageContent = data.content ?? []
    setLoadedPages((prev) => {
      if (prev[pageNumber]) return prev
      return { ...prev, [pageNumber]: pageContent }
    })
  }, [communitiesQuery.data])

  const aggregatedCommunities = useMemo(() => {
    const pageNumbers = Object.keys(loadedPages)
      .map((page) => Number(page))
      .filter((value) => Number.isFinite(value))
      .sort((a, b) => a - b)

    return pageNumbers.flatMap((page) => loadedPages[page] ?? [])
  }, [loadedPages])

  const sortedCommunities = useMemo(() => {
    const compareCommunities = (a: CommunityDTO, b: CommunityDTO) => {
      const isMemberA =
        typeof a.id === 'number' && joinedCommunityIds.has(a.id)
      const isMemberB =
        typeof b.id === 'number' && joinedCommunityIds.has(b.id)

      if (isMemberA !== isMemberB) {
        return isMemberA ? -1 : 1
      }

      const nameA = a.name ?? ''
      const nameB = b.name ?? ''

      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' })
    }

    return [...aggregatedCommunities].sort(compareCommunities)
  }, [aggregatedCommunities, joinedCommunityIds])

  const totalPages = Math.max(communitiesQuery.data?.data?.totalPages ?? 1, 1)
  const currentPage = communitiesQuery.data?.data?.number ?? discoverPage
  const hasNextPage = currentPage + 1 < totalPages
  const emptyMessage = searchQuery
    ? 'No communities match that search term. Try a different keyword.'
    : 'No communities available yet.'

  useEffect(() => {
    if (!communitiesQuery.isPending && isFetchingNext) {
      setIsFetchingNext(false)
    }
  }, [communitiesQuery.isPending, isFetchingNext])

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || !hasNextPage) return
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            hasNextPage &&
            !communitiesQuery.isPending &&
            !isFetchingNext
          ) {
            setIsFetchingNext(true)
            setDiscoverPage((prev) => Math.min(prev + 1, totalPages - 1))
          }
        })
      },
      { rootMargin: '200px' },
    )

    observer.observe(sentinel)

    return () => {
      observer.disconnect()
    }
  }, [hasNextPage, totalPages, communitiesQuery.isPending, isFetchingNext])
  const joinMutation = useJoinCommunity({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListMyCommunitiesQueryKey() })
        queryClient.invalidateQueries({ queryKey: getListCommunitiesQueryKey() })
        toast.success('Welcome to the community!')
      },
      onSettled: () => setActiveAction(null),
    },
  })

  const leaveMutation = useLeaveCommunity({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListMyCommunitiesQueryKey() })
        queryClient.invalidateQueries({ queryKey: getListCommunitiesQueryKey() })
        toast.success('You have left the community.')
      },
      onSettled: () => setActiveAction(null),
    },
  })

  const createCommunityMutation = useCreateCommunity({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListCommunitiesQueryKey() })
        queryClient.invalidateQueries({ queryKey: getListMyCommunitiesQueryKey() })
        toast.success('Community created. It will appear in discover shortly.')
        setCreateName('')
        setCreateDescription('')
        setCreateIsPublic(DEFAULT_CREATE_VISIBILITY)
        setCreateIconUrl('')
        setCreateCoverImageUrl('')
        setIconPreview(null)
        setCoverPreview(null)
        setIsCreateDialogOpen(false)
      },
      onError: () => {
        toast.error('We could not create the community right now. Please try again.')
      },
    },
  })

  const handleJoinCommunity = (communityId?: number) => {
    if (!communityId) return
    setActiveAction({ id: communityId, type: 'join' })
    joinMutation.mutate({ communityId })
  }

  const handleLeaveCommunity = (communityId?: number) => {
    if (!communityId) return
    setActiveAction({ id: communityId, type: 'leave' })
    leaveMutation.mutate({ communityId })
  }

  return (
    <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">Communities</h1>
            <p className="text-muted-foreground">
              Discover new spaces, manage the ones you created, and stay connected with peers.
            </p>
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm">Create community</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[90vh]">
              <DialogHeader>
                <DialogTitle>Create community</DialogTitle>
                <DialogDescription>
                  Name the space, describe why it exists, and choose whether it should be public.
                </DialogDescription>
              </DialogHeader>
              <ScrollArea className="max-h-[calc(90vh-12rem)] pr-4">
              <div className="space-y-6 pt-2">
                {/* Cover Image Upload */}
                <div className="space-y-3">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-0">
                    Cover Image
                  </Label>
                  <span className="text-xs opacity-70">Recommended: 1200×400px (3:1 ratio)</span>
                  <div className="relative">
                    <div className="group relative aspect-[3/1] w-full overflow-hidden rounded-lg border-2 border-dashed border-primary/20 bg-muted/50">
                      {coverPreview ? (
                        <>
                          <img
                            src={coverPreview}
                            alt="Cover preview"
                            className="h-full w-full object-cover"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                            <Button
                              type="button"
                              size="sm"
                              variant="destructive"
                              onClick={() => {
                                setCoverPreview(null)
                                setCreateCoverImageUrl('')
                              }}
                            >
                              <X className="mr-1 size-4" />
                              Remove
                            </Button>
                          </div>
                        </>
                      ) : (
                        <div className="flex h-full w-full items-center justify-center p-4">
                          <FilePondUpload
                            accept={['image/png', 'image/jpeg', 'image/jpg', 'image/webp']}
                            maxFileSize="20MB"
                            onUploadComplete={(_fileId, fileUrl) => {
                              setCreateCoverImageUrl(fileUrl)
                              setCoverPreview(fileUrl)
                              toast.success('Cover image uploaded')
                            }}
                            onUploadError={(error) => {
                              toast.error(`Upload failed: ${error}`)
                            }}
                            label={`
                              Upload Cover Image
                            `}
                          />
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      disabled={isReadOnly}
                      onClick={() => {
                        if (isReadOnly) return
                        fileInputRef.current?.click()
                      }}
                      className="group/avatar absolute -bottom-10 left-6 h-28 w-28 overflow-hidden rounded-full border-4 border-background bg-background shadow-lg transition-all hover:-translate-y-0.5 hover:border-primary/60 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60"
                      aria-label="Upload community logo"
                      title="Click to upload logo"
                    >
                      {iconPreview ? (
                        <img
                          src={iconPreview}
                          alt="School logo"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <img
                          src={`https://ui-avatars.com/api/?name=${encodeURIComponent(createName.trim() || 'Community')}&size=256`}
                          alt="Default community avatar"
                          className="h-full w-full object-cover"
                        />
                      )}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover/avatar:opacity-100">
                        <Camera className="h-9 w-9 text-white" strokeWidth={1.5} />
                      </div>
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      aria-label="Upload community logo"
                      title="Upload community logo"
                      className="hidden"
                      disabled={isReadOnly}
                      onChange={async (e) => {
                        if (isReadOnly) return
                        try {
                          const file = e.target.files?.[0]
                          if (!file) return
                          if (file.size > 20 * 1024 * 1024) {
                            toast.error(`Logo must be 20MB or smaller`)
                            return
                          }
                          const response = await uploadMutation.mutateAsync({ data: { file } })
                          const fileData = response.data
                          setIconPreview(fileData.path ?? null)
                        } catch (err) {
                          console.error('Logo upload failed', err)
                          toast.error('Failed to upload logo. Please try a different image.')
                        } finally {
                          if (fileInputRef.current) fileInputRef.current.value = ''
                        }
                      }}
                    />
                  </div>
                </div>

                <div className="space-y-2 text-sm mt-16">
                  <Label
                    htmlFor="community-name"
                    className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                  >
                    Name
                  </Label>
                  <Input
                    id="community-name"
                    placeholder="Community name"
                    value={createName}
                    onChange={(event) => setCreateName(event.target.value)}
                  />
                </div>
                <div className="space-y-2 text-sm">
                  <Label
                    htmlFor="community-description"
                    className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                  >
                    Description
                  </Label>
                  <Textarea
                    id="community-description"
                    rows={3}
                    placeholder="Tell people what this community is for"
                    value={createDescription}
                    onChange={(event) => setCreateDescription(event.target.value)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Visibility
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      Public communities are discoverable by everyone.
                    </p>
                  </div>
                  <Switch
                    checked={createIsPublic}
                    onCheckedChange={(checked) => setCreateIsPublic(Boolean(checked))}
                  />
                </div>
              </div>
              </ScrollArea>
              <DialogFooter className="space-y-2">
                {/* {createCommunityMutation.isError && (
                  <p className="text-xs text-destructive">
                    {createCommunityMutation.error?.message ??
                      'Unable to create community right now.'}
                  </p>
                )} */}
                <Button
                  onClick={() => {
                    if (!createName.trim()) {
                      toast.error('Community name is required.')
                      return
                    }
                    createCommunityMutation.mutate({
                      data: {
                        name: createName.trim(),
                        description: createDescription.trim() || undefined,
                        public: createIsPublic,
                        iconUrl: createIconUrl || undefined,
                        coverImageUrl: createCoverImageUrl || undefined,
                      },
                    })
                  }}
                  className="w-full justify-center"
                  disabled={createCommunityMutation.isPending}
                >
                  {createCommunityMutation.isPending && (
                    <Loader2 className="size-4 animate-spin" />
                  )}
                  Create community
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <div className="space-y-4">
          <Card>
            <CardHeader className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Users className="size-4 text-muted-foreground" />
                <CardTitle>Communities</CardTitle>
              </div>
              <p className="text-xs text-muted-foreground">
                Browse every community with your memberships pinned to the top.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label
                  htmlFor="community-search"
                  className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                >
                  Search communities
                </Label>
                <Input
                  id="community-search"
                  placeholder="Search by name, topic, or code"
                  value={discoverSearch}
                  onChange={(event) => setDiscoverSearch(event.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Search by name or topic, then page through the results below.
                </p>
              </div>
            {communitiesQuery.isError ? (
              <p className="text-sm text-destructive">
                {communitiesQuery.error?.message ??
                  'Unable to load communities. Please refresh to try again.'}
              </p>
            ) : (
              <>
                {communitiesQuery.isPending && sortedCommunities.length === 0 ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="size-4 animate-spin" />
                    <p>Loading communities…</p>
                  </div>
                ) : sortedCommunities.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{emptyMessage}</p>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {sortedCommunities.map((community) => {
                      const isMember =
                        typeof community.id === 'number' &&
                        joinedCommunityIds.has(community.id)
                      const actionLabel = isMember ? 'Leave' : 'Join'
                      const actionLoading =
                        activeAction?.id === community.id &&
                        activeAction?.type === (isMember ? 'leave' : 'join')

                      const handleAction = () =>
                        isMember
                          ? handleLeaveCommunity(community.id)
                          : handleJoinCommunity(community.id)

                      return (
                        <CommunityCard
                          key={community.id ?? community.name}
                          community={community}
                          isMember={isMember}
                          actionLabel={actionLabel}
                          actionLoading={actionLoading}
                          onAction={handleAction}
                          canVisit={isMember && typeof community.id === 'number'}
                        />
                      )
                    })}
                  </div>
                )}
                {isFetchingNext && (
                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground pt-4">
                    <Loader2 className="size-4 animate-spin" />
                    Loading more communities…
                  </div>
                )}
                <div ref={sentinelRef} aria-hidden="true" className="h-px" />
              </>
            )}
            </CardContent>
          </Card>
        </div>
    </div>
  )
}
